import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Text } from '@/components/ui/typography';
import { subscriptionStore } from '@/features/subscription/subscription.store';
import { uiStore } from '@/stores/ui.store';

import { teamApis } from '../team.query';
import { teamStore } from '../team.store';

import type { DiscoverableTeam } from '../team.types';

export const DiscoverableTeamBanner = observer(
  function DiscoverableTeamBanner() {
    const fetchedRef = useRef(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const reduceMotion = useReducedMotion();

    useEffect(() => {
      if (fetchedRef.current) return;
      fetchedRef.current = true;
      teamApis.fetchDiscoverableTeam();
    }, []);

    const team = teamStore.discoverableTeam;
    const alreadyInTeam = teamStore.dashboard != null;
    const dismissalKey = team != null ? `${team.id}:${team.source}` : null;
    const dismissed =
      dismissalKey != null &&
      uiStore.dismissedDiscoverableTeamKeys.includes(dismissalKey);

    const shouldShow = team != null && !alreadyInTeam && !dismissed;

    const isInvite = team?.source === 'invite';
    const joinLoading = isInvite
      ? teamStore.joinLoading
      : teamStore.joinByDomainLoading;

    const doJoin = () => {
      if (!team) return;
      if (isInvite && team.invite_token) {
        void teamApis.joinTeam(team.invite_token);
      } else {
        void teamApis.joinByDomain(team.id);
      }
    };

    const handleJoinClick = () => {
      if (subscriptionStore.isPlus) {
        setConfirmOpen(true);
      } else {
        doJoin();
      }
    };

    return (
      <>
        <AnimatePresence initial={!reduceMotion}>
          {shouldShow && team && (
            <motion.div
              key="discoverable-team-banner"
              initial={reduceMotion ? false : { x: '-50%', y: -8, opacity: 0 }}
              animate={{ x: '-50%', y: 0, opacity: 1 }}
              exit={
                reduceMotion
                  ? { x: '-50%', opacity: 0 }
                  : { x: '-50%', y: -8, opacity: 0 }
              }
              transition={{
                duration: reduceMotion ? 0 : 0.18,
                ease: 'easeOut',
              }}
              className="fixed top-5 left-1/2 z-50 w-full max-w-xl px-4 no-drag"
            >
              <XStack
                role="region"
                aria-label="Discoverable team"
                className="bg-surface bg-gradient-to-r from-brand-jordy-blueA/10 to-transparent border border-border rounded-lg shadow-sm pl-3 pr-3 py-3 gap-3 items-center"
              >
                <TeamInitialsAvatar name={team.name} />
                <YStack className="gap-0.5 min-w-0 flex-1">
                  <Text className="font-semibold leading-6 truncate">
                    {titleFor(team)}
                  </Text>
                  <Text size="sm" variant="subtle" className="truncate">
                    {subtitleFor(team)}
                  </Text>
                </YStack>
                <XStack className="gap-1 items-center shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      dismissalKey &&
                      uiStore.dismissDiscoverableTeam(dismissalKey)
                    }
                  >
                    Not now
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleJoinClick}
                    isLoading={joinLoading}
                  >
                    Join
                  </Button>
                </XStack>
              </XStack>
            </motion.div>
          )}
        </AnimatePresence>

        <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
          <Dialog.Content className="max-w-sm">
            <Dialog.Header>
              <Dialog.Title>Join {team?.name}?</Dialog.Title>
              <Dialog.Description>
                Your current paid subscription will be canceled and your access
                will be billed through {team?.name} going forward.
              </Dialog.Description>
            </Dialog.Header>
            <Dialog.Footer>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                isLoading={joinLoading}
                onClick={async () => {
                  if (!team) return;
                  try {
                    if (isInvite && team.invite_token) {
                      await teamApis.joinTeam(team.invite_token);
                    } else {
                      await teamApis.joinByDomain(team.id);
                    }
                    setConfirmOpen(false);
                  } catch {
                    /* toast already surfaced */
                  }
                }}
              >
                Join team
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Root>
      </>
    );
  }
);

function TeamInitialsAvatar({ name }: { name: string }) {
  return (
    <div
      aria-hidden
      className="size-8 shrink-0 rounded-full bg-brand-jordy-blueA/10 border border-brand-jordy-blueA/5 text-foreground flex items-center justify-center text-xs font-semibold uppercase select-none"
    >
      {initialsFor(name)}
    </div>
  );
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2);
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

function titleFor(team: DiscoverableTeam): string {
  return team.source === 'invite'
    ? `You’ve been invited to ${team.name}`
    : `${team.name} is on Littlebird`;
}

function subtitleFor(team: DiscoverableTeam): string {
  if (team.source === 'invite') {
    return 'Join your team to share context and chats.';
  }
  const noun = team.member_count === 1 ? 'person' : 'people';
  return `${team.member_count} ${noun} from your team — join to collaborate.`;
}
